import { useState } from "react";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { publicApi, errMsg } from "@/lib/api";

const ModeSwitch = ({ mode, setMode }) => (
  <div className="mt-6 grid grid-cols-2 rounded-full bg-slate-100 p-1 text-sm">
    {[["staff", "Staff passcode"], ["admin", "Admin login"]].map(([m, l]) => (
      <button key={m} type="button" onClick={() => setMode(m)} data-testid={`gate-mode-${m}`}
        className={`rounded-full py-2 font-medium transition-colors ${mode === m ? "bg-white text-[#0A3A6B] shadow-sm" : "text-slate-500"}`}>{l}</button>
    ))}
  </div>
);

export const PasscodeGate = ({ onSuccess }) => {
  const [mode, setMode] = useState("staff");
  const [code, setCode] = useState("");
  const [cred, setCred] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);

  const [pending, setPending] = useState(null);
  const [pw, setPw] = useState("");

  const finishLogin = (data) => {
    const role = data.user.role === "leader" ? "leader" : "admin";
    sessionStorage.setItem("vp_token", data.token);
    sessionStorage.setItem("vp_role", role);
    sessionStorage.setItem("vp_user", JSON.stringify({ ...data.user, must_change: false }));
    onSuccess(role, data.user);
  };

  const changePw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await publicApi.post("/auth/change-password", { current_password: cred.password, new_password: pw },
        { headers: { Authorization: `Bearer ${pending.token}` } });
      toast.success("Password saved. Welcome!");
      finishLogin({ token: data.token, user: pending.user });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "staff") {
        const { data } = await publicApi.post("/staff/verify", { passcode: code });
        sessionStorage.setItem("vp_passcode", code);
        sessionStorage.setItem("vp_role", data.role);
        onSuccess(data.role, null);
      } else {
        const { data } = await publicApi.post("/auth/login", cred);
        if (data.user.must_change) setPending(data);
        else finishLogin(data);
      }
    } catch (err) {
      toast.error(errMsg(err, "Sign-in failed"));
    } finally {
      setBusy(false);
    }
  };

  const ready = mode === "staff" ? code : cred.username && cred.password;
  if (pending) return (
    <div className="grain relative grid min-h-screen place-items-center bg-[#0A3A6B] px-5" data-testid="force-password-change">
      <form onSubmit={changePw} className="relative w-full max-w-sm rounded-3xl bg-white p-7 shadow-2xl sm:p-8">
        <Logo />
        <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900">Welcome, {pending.user.name.split(" ")[0]}!</h1>
        <p className="mt-1 text-sm text-slate-500">Please set your own password before continuing (at least 8 characters).</p>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="mt-6" data-testid="force-new-password-input" autoFocus />
        <Button type="submit" disabled={busy || pw.length < 8} className="mt-4 w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="force-password-save">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save and continue
        </Button>
      </form>
    </div>
  );
  return (
    <div className="grain relative grid min-h-screen place-items-center bg-[#0A3A6B] px-5" data-testid="passcode-gate">
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-3xl bg-white p-7 shadow-2xl sm:p-8">
        <Logo />
        <ModeSwitch mode={mode} setMode={setMode} />
        <div className="mt-6 grid h-12 w-12 place-items-center rounded-2xl bg-[#EBF3FA] text-[#0A3A6B]">
          {mode === "staff" ? <Lock className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
        </div>
        <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">{mode === "staff" ? "Staff Hub" : "Administrator"}</h1>
        <p className="mt-1 text-sm text-slate-500">{mode === "staff" ? "For pastors, staff and leaders." : "Manage the calendar, events and website pages."}</p>
        {mode === "staff" ? (
          <Input type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Passcode" className="mt-6" data-testid="passcode-input" autoFocus />
        ) : (
          <div className="mt-6 space-y-3">
            <Input value={cred.username} onChange={(e) => setCred({ ...cred, username: e.target.value })} placeholder="Username or email" autoCapitalize="none" data-testid="admin-username-input" />
            <Input type="password" value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} placeholder="Password" data-testid="admin-password-input" />
          </div>
        )}
        <Button type="submit" disabled={busy || !ready} className="mt-4 w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]"
          data-testid={mode === "staff" ? "passcode-submit-button" : "admin-login-button"}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {mode === "staff" ? "Enter" : "Sign in"}
        </Button>
      </form>
    </div>
  );
};
